/**
 * Inspector fields for the two OPVisual kinds. Kept out of InspectorPanel.tsx
 * (4200 lines) so the whole OPVisual authoring surface reads in one place.
 *
 * The machine ("Sends to") is NOT here: DEC-213 puts every destination at the
 * bottom of What it does, and InspectorPanel renders it there with the
 * single-machine form. What IS here is the auto-aim that fills that field in
 * when the answer is unambiguous, once per item (an effect that re-applied on
 * undo would turn Ctrl+Z into a no-op - same rule as the OBS auto-aim), and
 * the channel choice (`OpVisualSendsVia`) InspectorPanel places inside that
 * same Sends to zone.
 */

import { vizKnobNames } from '../timeline/opvControls.ts';
import { createEffect, For, Show } from 'solid-js';
import { InfoHint } from './InfoHint.tsx';
import { OpvPalettePicker } from './OpvPalettePicker.tsx';
import { editItem, type OpVisualGridItem, type OpVisualTriggerItem } from '../layout/store.ts';
import { HUE_ORDER, HUE_VALUES, type PaletteHue } from '../layout/projectStore.ts';
import { bridges } from '../opbridge/store.ts';
import { autoPickOpVisualBridge } from '../opvisual/machine.ts';
import { opvNotLinked } from '../opvisual/fire.ts';
import {
  OPVISUAL_COLS, OPVISUAL_LAYERS, DEFAULT_OPV_VIA, cellLabel, verbTakesQuant,
  type OpVisualAction, type OpVisualVerb, type OpvQuant,
} from '../opvisual/wire.ts';
import { Tip, TipRow } from './Tip.tsx';

const autoAimed = new Set<string>();

/** Aim an unaimed OPVisual item at the only machine it could mean. */
function useAutoAim(item: () => { id: string; bridgeIds?: string[] | undefined }): void {
  createEffect(() => {
    void bridges();
    const it = item();
    if (autoAimed.has(it.id)) return;
    // Only while never set: `[]` is an explicit None in "Sends to" and must
    // stick rather than be re-aimed.
    if (it.bridgeIds !== undefined) return;
    const pick = autoPickOpVisualBridge(it.id);
    if (!pick) return;
    autoAimed.add(it.id);
    editItem(it.id, { bridgeIds: [pick] });
  });
}

const VERBS: Array<{ verb: OpVisualVerb; label: string; group: string }> = [
  { verb: 'cell',          label: 'Fire a cell',          group: 'Grid' },
  { verb: 'column',        label: 'Fire a column',        group: 'Grid' },
  { verb: 'clearLayer',    label: 'Clear a layer',        group: 'Grid' },
  { verb: 'stopAll',       label: 'Stop all layers',      group: 'Grid' },
  { verb: 'chainToggle',   label: 'Chain start / stop',   group: 'Chain' },
  { verb: 'chainStart',    label: 'Chain start',          group: 'Chain' },
  { verb: 'chainStop',     label: 'Chain stop',           group: 'Chain' },
  { verb: 'chainNext',     label: 'Chain next',           group: 'Chain' },
  { verb: 'chainGoto',     label: 'Chain go to column',   group: 'Chain' },
  { verb: 'blackout',      label: 'Blackout on / off',    group: 'Output' },
  { verb: 'operatorScreen', label: 'Hide / show OPVisual screen', group: 'Output' },
  { verb: 'panic',         label: 'Panic',                group: 'Output' },
  { verb: 'reloadLibrary', label: 'Reload clip library',  group: 'Output' },
  { verb: 'vizVariation',  label: 'New take',             group: 'Visualiser' },
  { verb: 'vizMacro',      label: 'Set a knob',           group: 'Visualiser' },
  { verb: 'palette',       label: 'Show palette',         group: 'Visualiser' },
  { verb: 'streamSafe',    label: 'Stream safe',          group: 'Visualiser' },
];

/** Switch verb, carrying the col / layer across so changing "cell" to
 *  "column" keeps the column the operator was already looking at. */
function withVerb(prev: OpVisualAction, verb: OpVisualVerb): OpVisualAction {
  const col = 'col' in prev ? prev.col : 1;
  // A visualiser verb's layer may be 0 ("the newest"), which no grid verb takes.
  const layer = 'layer' in prev && prev.layer >= 1 ? prev.layer : 1;
  // Follow off carries across cell <-> column, like the column does.
  const nf = 'follow' in prev && prev.follow === false ? { follow: false as const } : {};
  switch (verb) {
    case 'cell':       return { verb, col, layer, ...nf };
    case 'column':     return { verb, col, ...nf };
    case 'chainGoto':  return { verb, col };
    case 'clearLayer': return { verb, layer };
    case 'vizVariation': return { verb, layer: 0 };
    case 'vizMacro':   return { verb, macro: 1, value: 0.5, layer: 0 };
    case 'palette':    return { verb, id: '' };
    case 'streamSafe': return { verb, mode: 'toggle' };
    default:           return { verb } as OpVisualAction;
  }
}

const cols = Array.from({ length: OPVISUAL_COLS }, (_, i) => i + 1);
const layersTopDown = Array.from({ length: OPVISUAL_LAYERS }, (_, i) => OPVISUAL_LAYERS - i);

export function OpVisualTriggerFields(props: { item: OpVisualTriggerItem }) {
  useAutoAim(() => props.item);
  const a = () => props.item.action;
  const set = (action: OpVisualAction) => editItem(props.item.id, { action });
  const overOsc = () => (props.item.sendVia ?? DEFAULT_OPV_VIA) === 'osc';
  const keepFollow = (): { follow?: false } => {
    const x = a();
    return (x.verb === 'cell' || x.verb === 'column') && x.follow === false ? { follow: false } : {};
  };

  return (
    <div class="inspector-zone">
      <label class="inspector-field">
        <span>Does</span>
        <select
          value={a().verb}
          onChange={(e) => set(withVerb(a(), e.currentTarget.value as OpVisualVerb))}
        >
          <For each={['Grid', 'Chain', 'Output', 'Visualiser']}>
            {(group) => (
              <optgroup label={group}>
                <For each={VERBS.filter((v) => v.group === group)}>
                  {(v) => <option value={v.verb}>{v.label}</option>}
                </For>
              </optgroup>
            )}
          </For>
        </select>
      </label>

      {/* Cell: the whole grid, laid out as OPVisual shows it (L5 on top). */}
      <Show when={a().verb === 'cell'}>
        <div class="opv-pick" data-mode="cell">
          <span class="opv-pick-title">
            Cell {a().verb === 'cell' ? cellLabel((a() as { col: number }).col, (a() as { layer: number }).layer) : ''}
          </span>
          <div class="opv-pick-grid" style={{ '--opv-cols': String(OPVISUAL_COLS) }}>
            <span />
            <For each={cols}>{(c) => <span class="opv-pick-head">{c}</span>}</For>
            <For each={layersTopDown}>
              {(layer) => (
                <>
                  <span class="opv-pick-head">L{layer}</span>
                  <For each={cols}>
                    {(col) => {
                      const on = () => {
                        const x = a();
                        return x.verb === 'cell' && x.col === col && x.layer === layer;
                      };
                      return (
                        <button
                          type="button"
                          class="opv-pick-cell"
                          data-selected={on() ? 'true' : 'false'}
                          title={cellLabel(col, layer)}
                          aria-label={cellLabel(col, layer)}
                          onClick={() => set({ verb: 'cell', col, layer, ...keepFollow() })}
                        />
                      );
                    }}
                  </For>
                </>
              )}
            </For>
          </div>
        </div>
      </Show>

      <Show when={a().verb === 'column' || a().verb === 'chainGoto'}>
        <div class="inspector-pills">
          <span class="inspector-pills-label">Column</span>
          <For each={cols}>
            {(col) => (
              <button
                type="button"
                class="inspector-pill"
                data-selected={'col' in a() && (a() as { col: number }).col === col ? 'true' : 'false'}
                onClick={() => set(withVerb({ verb: 'column', col, ...keepFollow() }, a().verb))}
              >{col}</button>
            )}
          </For>
        </div>
      </Show>

      <Show when={a().verb === 'clearLayer'}>
        <div class="inspector-pills">
          <span class="inspector-pills-label">Layer</span>
          <For each={[...layersTopDown].reverse()}>
            {(layer) => (
              <button
                type="button"
                class="inspector-pill"
                data-selected={'layer' in a() && (a() as { layer: number }).layer === layer ? 'true' : 'false'}
                onClick={() => set({ verb: 'clearLayer', layer })}
              >L{layer}</button>
            )}
          </For>
        </div>
      </Show>

      <Show when={a().verb === 'vizVariation' || a().verb === 'vizMacro'}>
        <div class="inspector-pills" data-viz-layer>
          <span class="inspector-pills-label">Which</span>
          <For each={[0, 1, 2, 3, 4, 5]}>
            {(layer) => (
              <button
                type="button"
                class="inspector-pill"
                data-selected={'layer' in a() && (a() as { layer: number }).layer === layer ? 'true' : 'false'}
                onClick={() => set({ ...(a() as Extract<OpVisualAction, { verb: 'vizVariation' | 'vizMacro' }>), layer })}
              >{layer === 0 ? 'Newest' : `L${layer}`}</button>
            )}
          </For>
        </div>
      </Show>

      <Show when={a().verb === 'vizMacro'}>
        <label class="inspector-field" data-viz-macro>
          <span>Knob</span>
          <select
            value={String((a() as { macro?: number }).macro ?? 1)}
            onChange={(e) => set({ ...(a() as Extract<OpVisualAction, { verb: 'vizMacro' }>), macro: Number(e.currentTarget.value) })}
          >
            <For each={[1, 2, 3, 4, 5, 6, 7]}>
              {(macro) => <option value={String(macro)} selected={(a() as { macro?: number }).macro === macro}>{vizKnobNames(macro)}</option>}
            </For>
          </select>
        </label>
        <label class="inspector-field">
          <span>To {Math.round(((a() as { value?: number }).value ?? 0) * 100)}%</span>
          <input
            type="range" min="0" max="100" step="1"
            value={Math.round(((a() as { value?: number }).value ?? 0) * 100)}
            onChange={(e) => set({ ...(a() as Extract<OpVisualAction, { verb: 'vizMacro' }>), value: Number(e.currentTarget.value) / 100 })}
          />
        </label>
        <InfoHint title="Visualiser knobs" label="What this does">
          <p>Sets a knob on the visualiser to this value. Each visualiser names its own knobs, so one choice here is a different knob on each (Life on Genesis, Liquid on Liquid Chrome). Crowd is Laser Rig's only.</p>
          <p>Live only: the next time that visualiser is fired, its saved knobs come back.</p>
        </InfoHint>
      </Show>

      <Show when={a().verb === 'palette'}>
        <div class="inspector-field" data-opv-palette>
          <span>Palette</span>
          <OpvPalettePicker
            target={props.item.bridgeIds?.[0] ?? autoPickOpVisualBridge(props.item.id) ?? undefined}
            value={(a() as { id?: string }).id ?? ''}
            onPick={(id) => set({ verb: 'palette', id })}
          />
        </div>
        <InfoHint title="Show palette" label="What this does">
          <p>Changes OPVisual's show palette, the colours every visualiser draws with. It fades over 2 seconds.</p>
          <p>The list is OPVisual's own; "On now" marks the one it is showing. It is separate from a timeline's show colour.</p>
        </InfoHint>
      </Show>

      <Show when={a().verb === 'streamSafe'}>
        <div class="inspector-pills" data-stream-safe>
          <span class="inspector-pills-label">Press</span>
          <For each={['toggle', 'on', 'off'] as const}>
            {(mode) => (
              <button
                type="button"
                class="inspector-pill"
                data-selected={a().verb === 'streamSafe' && (a() as { mode: string }).mode === mode ? 'true' : 'false'}
                disabled={mode === 'off' && overOsc()}
                title={mode === 'off' && overOsc() ? 'OPVisual never turns Stream safe off over OSC. Send through OPBridge to use this.' : undefined}
                onClick={() => set({ verb: 'streamSafe', mode })}
              >{mode === 'toggle' ? 'Switches' : mode === 'on' ? 'Turns on' : 'Turns off'}</button>
            )}
          </For>
        </div>
        {/* OPVisual b55333b (2026-10-12): its OSC listener is open to the LAN with no login, and Stream safe protects
            viewers, so over OSC it can only be turned ON - "Switches" always turns it on, "Turns off" is refused. */}
        <Show when={overOsc()}>
          <div class="inspector-notice" data-tone="warn" role="status" data-stream-safe-osc>
            <span class="inspector-notice-head">Over OSC this can only turn Stream safe on</span>
            <span class="inspector-notice-body">
              {(a() as { mode?: string }).mode === 'off'
                ? 'Turns off does nothing over OSC: OPVisual refuses it, to protect viewers.'
                : (a() as { mode?: string }).mode === 'toggle'
                  ? 'Switches always turns it on over OSC: OPVisual refuses turning it off, to protect viewers.'
                  : 'Turns on works over OSC. Turning it off only works when this sends through OPBridge.'}
              {' '}Set Sends via to OPBridge for all three.
            </span>
          </div>
        </Show>
        <InfoHint title="Stream safe" label="What this does">
          <p>Less fine detail (particles, grain) across the whole picture, so a Twitch stream does not turn to mush.</p>
        </InfoHint>
      </Show>

      <Show when={verbTakesQuant(a().verb)}>
        <QuantPills id={props.item.id} quant={props.item.quant} />
      </Show>

      <Show when={a().verb === 'blackout'}>
        <InfoHint title="OPVisual blackout">
          <p>OPVisual's own blackout: each press switches it on or off.</p>
          <p>Raising the master also brings the picture back.</p>
        </InfoHint>
      </Show>
      <Show when={a().verb === 'operatorScreen'}>
        <InfoHint title="Operator screen" label="What this does">
          <p>Hides OPVisual's control window behind its small pill, or brings it back.</p>
          <p>Works any time, in or out of GIG - handy from a pedal with nobody at the mouse.</p>
        </InfoHint>
      </Show>
      <Show when={a().verb === 'panic'}>
        <InfoHint title="OPVisual panic" label="What this does" short="Worth turning on Guard below.">
          <p>OPVisual's full reset: clears every layer and stops the chain, so the screen goes black.</p>
          <p>The look goes back to neutral at full master, and a blackout is released, so the next thing you fire is seen.</p>
        </InfoHint>
      </Show>
      <Show when={a().verb === 'cell' || a().verb === 'column'}>
        <FollowToggle
          on={(a() as { follow?: false }).follow !== false}
          onChange={(on) => {
            const x = a() as Extract<OpVisualAction, { verb: 'cell' | 'column' }>;
            const { follow: _drop, ...rest } = x;
            void _drop;
            set((on ? rest : { ...rest, follow: false }) as OpVisualAction);
          }}
        />
        <InfoHint title="What the button shows">
          <p>The button shows "sent" after a fire.</p>
          <p>It cannot show what is actually on air yet - OPVisual does not report that
          to OPButtons.</p>
        </InfoHint>
      </Show>
    </div>
  );
}

export function OpVisualGridFields(props: { item: OpVisualGridItem }) {
  useAutoAim(() => props.item);
  return (
    <div class="inspector-zone">
      <QuantPills id={props.item.id} quant={props.item.quant} />
      <label class="inspector-field" title={`How many columns to show, from column 1. OPVisual has ${OPVISUAL_COLS}.`}>
        <span>Columns</span>
        <input
          type="number" min="1" max={OPVISUAL_COLS}
          value={props.item.cols}
          onInput={(e) => {
            const n = Math.round(Number(e.currentTarget.value));
            if (n >= 1 && n <= OPVISUAL_COLS) editItem(props.item.id, { cols: n });
          }}
        />
      </label>
      <label class="inspector-toggle" title="On/off, blend, speed, clear and opacity in each layer's row">
        <input
          type="checkbox"
          checked={props.item.showLayerControls}
          onInput={(e) => editItem(props.item.id, { showLayerControls: e.currentTarget.checked })}
        />
        <span>Layer controls</span>
      </label>
      <label class="inspector-toggle" title="Master opacity plus brightness, hue, saturation and bloom">
        <input
          type="checkbox"
          checked={props.item.showMasterLook}
          onInput={(e) => editItem(props.item.id, { showMasterLook: e.currentTarget.checked })}
        />
        <span>Master and look</span>
      </label>
      <label class="inspector-toggle" title="Six knobs for one layer's visualiser, and NEW TAKE">
        <input
          type="checkbox"
          checked={props.item.showViz !== false}
          onInput={(e) => editItem(props.item.id, { showViz: e.currentTarget.checked })}
        />
        <span>Visualiser knobs</span>
      </label>
      <label class="inspector-toggle" title="OPVisual's live output, shown beside the knobs while this grid is on screen">
        <input
          type="checkbox"
          checked={props.item.showMonitor !== false}
          onInput={(e) => editItem(props.item.id, { showMonitor: e.currentTarget.checked })}
        />
        <span>Program monitor</span>
      </label>
      <FollowToggle on={props.item.follow !== false} onChange={(on) => editItem(props.item.id, { follow: on ? undefined : false })} />
      {/* Each layer's own colour (2026-10-18). Rows top-down like the grid's;
          the first chip is "same as the grid", which stores nothing. */}
      <div class="inspector-layer-hues" data-testid="opv-layer-hues">
        <For each={Array.from({ length: OPVISUAL_LAYERS }, (_, i) => OPVISUAL_LAYERS - i)}>
          {(layer) => {
            const cur = () => props.item.layerHues?.[layer - 1] ?? 'none';
            const pick = (h: PaletteHue) => {
              const next = Array.from({ length: OPVISUAL_LAYERS }, (_, i) => props.item.layerHues?.[i] ?? null);
              next[layer - 1] = h === 'none' ? null : h;
              editItem(props.item.id, { layerHues: next.some((x) => x) ? next : undefined });
            };
            return (
              <div class="inspector-pills" data-layer={layer}>
                <span class="inspector-pills-label">{`Layer ${layer}`}</span>
                {HUE_ORDER.map((h) => (
                  <button
                    type="button"
                    class="inspector-swatch sk-swatch"
                    data-hue={h}
                    style={{ '--swatch': HUE_VALUES[h] }}
                    data-selected={cur() === h ? 'true' : 'false'}
                    onClick={() => pick(h)}
                    aria-label={h === 'none' ? `Layer ${layer}: the grid's colour` : `Layer ${layer}: ${h}`}
                    title={h === 'none' ? "The grid's colour" : h}
                  />
                ))}
              </div>
            );
          }}
        </For>
      </div>
      <InfoHint title="What the cells show">
        <p>Each cell shows the clip OPVisual has in it, with its picture.</p>
        <p>ON AIR and NEXT BAR come from OPVisual itself, when it is running and reaching
        OPBridge. Without that, a cell shows SENT: what this grid last fired.</p>
      </InfoHint>
    </div>
  );
}

/** Follow (2026-10-18, OPVisual DEC-148). On: a fired cell runs its own
 *  "then" in OPVisual (go right, down, a named cell...). Off: it counts its
 *  plays and ends there. Hand-pressed items default to on. */
function FollowToggle(props: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <label class="inspector-toggle" data-testid="opv-follow"
      title="On: a cell set in OPVisual to play N times and then move on does move on. Off: it plays its N times and stops.">
      <input type="checkbox" checked={props.on} onInput={(e) => props.onChange(e.currentTarget.checked)} />
      <span>Follow on to the next cell</span>
    </label>
  );
}

function QuantPills(props: { id: string; quant: OpvQuant }) {
  return (
    <div class="inspector-pills">
      <span class="inspector-pills-label">Trigger</span>
      <button
        type="button"
        class="inspector-pill"
        data-selected={props.quant === 'bar' ? 'true' : 'false'}
        onClick={() => editItem(props.id, { quant: 'bar' })}
        title="On the next bar line, following OPBridge's clock"
      >Next bar</button>
      <button
        type="button"
        class="inspector-pill"
        data-selected={props.quant === 'now' ? 'true' : 'false'}
        onClick={() => editItem(props.id, { quant: 'now' })}
        title="The moment you tap it"
      >Instant</button>
    </div>
  );
}

/**
 * "Sends via" (DEC-215): how this item reaches OPVisual. Rendered by
 * InspectorPanel INSIDE the Sends to zone, under the machine, as the same
 * vertical 44px stack (DEC-213). OPBridge is the default and the only row that
 * stores nothing (`sendVia` absent), so choosing it is also the way back to
 * whatever the default becomes.
 *
 * The hint under it is the whole explanation a novice gets, so it says what
 * each choice COSTS, and - when the last press found no OPVisual - what to do.
 */
export function OpVisualSendsVia(props: { item: OpVisualTriggerItem | OpVisualGridItem }) {
  const via = () => props.item.sendVia ?? DEFAULT_OPV_VIA;
  const notLinked = () => opvNotLinked({ bridgeIds: props.item.bridgeIds, via: props.item.sendVia });
  return (
    <>
      <div class="inspector-pills" data-stack="true" data-sends-via="true">
        <span class="inspector-pills-label">Sends via</span>
        <button
          type="button"
          class="inspector-pill"
          data-via="opbridge"
          data-selected={via() === 'opbridge' ? 'true' : 'false'}
          onClick={() => editItem(props.item.id, { sendVia: undefined })}
          title="OPBridge protocol - tells you when OPVisual is not there to hear it"
        >OPBridge</button>
        <button
          type="button"
          class="inspector-pill"
          data-via="osc"
          data-selected={via() === 'osc' ? 'true' : 'false'}
          onClick={() => editItem(props.item.id, { sendVia: 'osc' })}
          title="OSC straight to OPVisual's port 7011 on that computer - nothing reports back"
        >OSC</button>
      </div>
      <Show
        when={via() === 'osc'}
        fallback={
          <Show
            when={notLinked()}
            fallback={<TipRow label="Through OPBridge" text="Tells you if OPVisual is not there to hear a press." />}
          >
            <div class="inspector-notice" data-tone="fault" role="status">
              <span class="inspector-notice-head">OPVisual not linked</span>
              <span class="inspector-notice-body">
                The last press found no OPVisual linked to OPBridge on that computer, so nothing happened.
                Open OPVisual there and check its bridge status reads LINKED, or choose OSC.
              </span>
            </div>
          </Show>
        }
      >
        <TipRow label="Straight to OSC" text="Straight to OPVisual's OSC port. Works even when OPVisual is not linked to OPBridge, but nothing reports back: if OPVisual is closed, presses are lost without a warning." />
      </Show>
    </>
  );
}
